import { useQuery } from '@apollo/client';
import { Link, Navigate } from 'react-router-dom';
import { Spinner, ErrorLocal, NotImplemented } from 'components';
import { QuizTypes, SessionTypes } from 'consts';
import { useQuizContext, useQuizSessionContext, useEventContext } from 'contexts';
import { getQuizQuestions } from 'graphql/queries';
import { QuizQuestions, QuizQuestionsVariables } from 'graphql/queries/types/QuizQuestions';
import { BattleshipQuiz } from 'pages/battleship-quiz/battleship-quiz';
import { JeopardyBase } from 'pages/jeopardy-quiz';
import { SharpshooterQuiz } from 'pages/sharpshooter/sharpshooter-quiz';
import { SharpshooterQuizSetup } from 'pages/sharpshooter/sharpshooter-types';
import { BattleshipGameTypes, BattleshipQuizSetup } from '../battleship-quiz/types';
import { NavPages, NavUtils } from 'utils';
import { QuickAnswer } from './quick-answer/quick-answer';
import { QuizSessionSplashScreen } from './quiz-session-splash-screen';
import { useState } from 'react';

interface Props {}

/**
 * Start the appropriate quiz given the quiz session key.
 * This component fetches the quiz setup, starts the user session and starts the quiz.
 * @param props
 * @returns
 */
export const QuizSessionQuiz = (props: Props) => {
    const { quiz } = useQuizContext();
    const event = useEventContext();
    const sessionContext = useQuizSessionContext();
    const [showSplashScreen, setShowSplashScreen] = useState(true);

    const {
        loading: loadingQuestions,
        error: errorLoadingQuestions,
        data: quizQuestions
        // refetch
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quiz.id! }
    });

    if (loadingQuestions) return <Spinner />;
    if (errorLoadingQuestions) return <ErrorLocal errorMessage="Error getting session or quiz data :(" />;
    if (!quizQuestions) return <p>No quiz questions</p>;

    if (sessionContext?.session.quizId !== quiz.id) {
        return <Navigate to={NavUtils.getQuizSelectorPath(event?.alias)} replace />;
    }

    const onStartQuiz = () => { setShowSplashScreen(false); };

    const quizSession = sessionContext?.session;
    const quizType = quiz.quizTypeId as QuizTypes;

    if (!quizSession?.isOpen) {
        return (
            <div className="aq-content">
                <h3>Session is not active</h3>
                <p>
                    This session link is no longer active. You can return to the quiz start page or go back to the quiz list.
                </p>
                <p>
                    <Link to={NavUtils.generateNavPath(NavPages.Quiz, quiz.id, event?.alias)}>
                        Go to quiz start page
                    </Link>
                </p>
                <p>
                    <Link to={NavUtils.getQuizSelectorPath(event?.alias)}>
                        Go to quiz list
                    </Link>
                </p>
            </div>
        );
    }

    const quizScenes = {
        [QuizTypes.Battleship]: () => {
            const quizSettings = quizSession!.quizSettings as BattleshipQuizSetup;
            const gameType = quizSettings.gameType ?? BattleshipGameTypes.REMOTE_COMPETITION;
            return (
                <BattleshipQuiz
                    gridColumns={quizSettings.columns}
                    gridRows={quizSettings.rows}
                    teams={quizSettings.teams}
                    quizQuestions={quizQuestions!}
                    cooldownSeconds={quizSettings.cooldownSec}
                    gameType={gameType}
                />
            );
        },
        [QuizTypes.Sharpshooter]: () => {
            const quizSettings = quizSession!.quizSettings as SharpshooterQuizSetup;
            return (
                <SharpshooterQuiz
                    gridColumns={quizSettings.columns}
                    gridRows={quizSettings.rows}
                    teams={quizSettings.teams}
                    quizQuestions={quizQuestions!}
                    isSinglePlayer={true}
                />
            );
        },
        [QuizTypes.Slideshow]: () => <NotImplemented />,
        [QuizTypes.Jeopardy]: () => <JeopardyBase />
    };

    const shouldShowSplashScreen = showSplashScreen && quizType !== QuizTypes.Jeopardy;

    switch (quizSession?.type) {
        case SessionTypes.LivePaperless:
        case SessionTypes.LiveQuickAnswer:
            return <QuickAnswer quizQuestions={quizQuestions}/>;
        case SessionTypes.Distributed:
        default:
            return shouldShowSplashScreen ? <QuizSessionSplashScreen onStartQuiz={onStartQuiz} /> : quizScenes[quizType]();
    }
};
